import axios from 'axios'
import { getLocalStorage } from './localStorage'
import { clearLogInStorage } from '../container/App/actions'
import { isSessionDeadError } from './sessionError'

/**
 * Global axios response interceptor — auto-logout on a stale session.
 *
 * Every authed call in the app uses the default `axios` singleton (no
 * per-file instance), so registering once here covers all call sites.
 *
 * Guard: only force logout when the user *thinks* they are logged in
 * (`login.isLoggedIn === true`) AND the API rejects with a session-dead error.
 * The backend returns 403 (not 401) for a stale/expired token — see
 * `isSessionDeadError` in ./sessionError for the exact rules. A 401/403 while
 * logged-out (e.g. wrong password on the login screen) is left untouched so the
 * normal error UX still runs, and a business-403 (permission/validation) is
 * ignored so it doesn't nuke the session. clearLogInStorage() + reload mirrors
 * the existing logout() thunk; after the reload isLoggedIn is false, so a
 * follow-up error can't trigger a reload loop.
 */
export const setupAxiosInterceptors = (): void => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status
      const message = error?.response?.data?.error?.message
      if (isSessionDeadError(status, message, getLocalStorage()?.login?.isLoggedIn)) {
        clearLogInStorage()
        window.location.reload()
      }
      return Promise.reject(error)
    }
  )
}
